import { apiAnalytics } from "@/api/analytics/analytics.api.ts";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { TimeOffBalancesByCategoryTable } from "@/pages/employees/time-off/balances/components/TimeOffBalancesByCategoryTable.tsx";
import { TimeOffBalancesSummaryTable } from "@/pages/employees/time-off/balances/components/TimeOffBalancesSummaryTable.tsx";
import {
  useTimeOffAnalyticsBalancesByCategory,
  useTimeOffAnalyticsBalancesSummary,
} from "@/queries/analytics/use-time-off-analytics.query.ts";
import { useTimeOffCategories } from "@/queries/use-time-off.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiAnalyticsTimeOffBalanceFilter,
  ApiAnalyticsTimeOffBalanceSummaryFilter,
} from "@/types/analytics/analytics.time-off.types.ts";
import { AnalyticReportTypes, ApiExportFormat } from "@/types/analytics/analytics.types.ts";
import { ApiSorting, ApiSortingDirection } from "@/types/api.types.ts";
import { EmployeeColumnId } from "@/types/column.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Button,
  CoreFilterType,
  DropdownMenu,
  DropdownMenuItem,
  FilterRenderType,
  FilterValueType,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  Stack,
  TabItem,
  Tabs,
} from "@skailer-com/uikit";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export const TimeOffBalancesReport = () => {
  const { t } = useTranslation();
  const { data: categories } = useTimeOffCategories({ enabled: true });
  const { id } = useParams();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  useTitle(t("common:balances"), t("time_off:time_off"));

  const { isExporting, downloadFile } = useDownloadHelper();
  const { canIEdit } = usePermissions();

  const handleTabChanged = (tabIndex: number) => {
    const currentSearchParams = new URLSearchParams(window.location.search);

    if (tabIndex === 0) navigate("/time-off/balances-report?" + currentSearchParams);

    const category = categories?.[tabIndex - 1];
    if (category) navigate(`/time-off/balances-report/${category.id}?` + currentSearchParams);
  };

  const activeCategoryId = useMemo(() => {
    if (id) {
      analyticsService.trackEvent(analyticEvents.reporting.viewed, {
        [analyticProperties.title]: AnalyticReportTypes.timeOffBalancesByCategory,
      });
    } else {
      analyticsService.trackEvent(analyticEvents.reporting.viewed, {
        [analyticProperties.title]: AnalyticReportTypes.timeOffBalancesSummary,
      });
    }

    if (!id) return undefined;
    return Number(id);
  }, [id]);

  const activeTabIndex = useMemo(() => {
    if (activeCategoryId === undefined) return 0;

    const categoryIndex = categories?.findIndex((cat) => cat.id === activeCategoryId);
    return categoryIndex !== undefined && categoryIndex >= 0 ? categoryIndex + 1 : 0;
  }, [activeCategoryId, categories]);

  const activeReportType = useMemo(() => {
    if (activeCategoryId == undefined) {
      return AnalyticReportTypes.timeOffBalancesSummary;
    }
    return AnalyticReportTypes.timeOffBalancesByCategory;
  }, [activeCategoryId]);

  const library = filtersLibrary();
  const {
    filter: { filter: filterSummary, typedFilters },
    filterButton: filterButtonSummary,
    onApplySorting,
    isFilterReady: isSummaryFilterReady,
  } = useAppFilter<ApiAnalyticsTimeOffBalanceSummaryFilter>({
    filters: [
      library.page_number,
      library.text,
      library.date_to,
      library.employee_ids,
      library.job_profile_ids,
      library.job_role_ids,
      library.department_ids,
      library.division_ids,
      library.location_ids,
      library.employee_lifecycle_statuses,
      {
        ...library.sort,
        defaultValue: {
          fields: [
            {
              field: EmployeeColumnId.employee_name,
              direction: ApiSortingDirection.asc,
            },
          ],
        } as ApiSorting,
      },
    ],
    source: AnalyticReportTypes.timeOffBalancesSummary,
  });
  const {
    filter: { filter: filterByCategory, typedFilters: typedFiltersByCategory },
    filterButton: filterByCategoryButton,
    onApplySorting: onApplySortingByCategory,
    isFilterReady: isCategoryFilterReady,
  } = useAppFilter<ApiAnalyticsTimeOffBalanceFilter>({
    filters: [
      {
        name: "time_off_category_id",
        type: CoreFilterType.param,
        searchParamName: "id",
        valueType: FilterValueType.number,
        renderType: FilterRenderType.none,
      },
      library.page_number,
      library.text,
      library.date_from,
      library.date_to,
      library.employee_ids,
      library.job_profile_ids,
      library.job_role_ids,
      library.department_ids,
      library.division_ids,
      library.location_ids,
      library.employee_lifecycle_statuses,
      {
        ...library.sort,
        defaultValue: {
          fields: [
            {
              field: EmployeeColumnId.employee_name,
              direction: ApiSortingDirection.asc,
            },
          ],
        } as ApiSorting,
      },
    ],
    source: AnalyticReportTypes.timeOffBalancesByCategory,
  });

  const [searchTerm, setSearchTerm] = useState<string | undefined>(typedFilters.search);

  const { data: summaryReport, isLoading: isLoadingSummary } = useTimeOffAnalyticsBalancesSummary({
    filter: filterSummary,
    enabled: activeCategoryId == undefined && isSummaryFilterReady,
  });
  const { data: byCategoryReport, isLoading: isLoadingByCategory } =
    useTimeOffAnalyticsBalancesByCategory({
      filter: filterByCategory,
      enabled: !!activeCategoryId && isCategoryFilterReady,
    });

  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (newData) => {
      setSearchParams((searchParams) => {
        if (newData) searchParams.set("search", newData);
        else searchParams.delete("search");
        searchParams.set("page", "1");
        return searchParams;
      });
    },
  });

  const handleExportClick = useCallback(
    (format: ApiExportFormat) => {
      if (activeReportType == AnalyticReportTypes.timeOffBalancesSummary) {
        const filterToExport: ApiAnalyticsTimeOffBalanceSummaryFilter = {
          ...filterSummary,
          format: format,
        };

        downloadFile({
          apiCall: () => apiAnalytics.timeOff.balancesSummaryExport(filterToExport),
          onSuccess: () => {
            analyticsService.trackEvent(analyticEvents.dataExported, {
              [analyticProperties.title]: activeReportType,
            });
          },
        });
      } else {
        const filterToExport: ApiAnalyticsTimeOffBalanceFilter = {
          ...filterByCategory,
          format: format,
        };

        downloadFile({
          apiCall: () => apiAnalytics.timeOff.balancesReportExport(filterToExport),
          onSuccess: () => {
            analyticsService.trackEvent(analyticEvents.dataExported, {
              [analyticProperties.title]: activeReportType,
              [analyticProperties.format]: format,
            });
          },
        });
      }
    },
    [activeReportType, filterByCategory, filterSummary]
  );

  return (
    <>
      <Page className={"h-full"}>
        <Page.Header title={t("common:balances")}>
          <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
            {canIEdit(ApiPermission.reporting_export) && (
              <DropdownMenu
                trigger={
                  <Button isLoading={isExporting} variant={"secondary"}>
                    {`${t("common:export")}...`}
                  </Button>
                }
              >
                <DropdownMenuItem
                  onClick={() => handleExportClick(ApiExportFormat.xlsx)}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-file-xls" />}
                  title={t("common:export_to_xlsx")}
                />
              </DropdownMenu>
            )}
            {activeReportType == AnalyticReportTypes.timeOffBalancesSummary &&
              filterButtonSummary()}
            {activeReportType == AnalyticReportTypes.timeOffBalancesByCategory &&
              filterByCategoryButton()}
          </Stack>
        </Page.Header>

        <Page.Content stretch>
          <Tabs fill onActiveTabChange={handleTabChanged} activeTab={activeTabIndex}>
            <TabItem
              title={t("reporting:all_balances")}
              className={"flex min-h-0 min-w-0 flex-1 flex-col"}
            >
              <Page.StickyLayout
                toolbar={
                  <Search
                    onInput={(e) => {
                      setSearchTerm(e.currentTarget.value);
                    }}
                    type={"text"}
                    value={searchTerm}
                    placeholder={t("common:search")}
                    clearable={true}
                    onClear={() => {
                      setSearchTerm(undefined);
                    }}
                  />
                }
                footer={
                  summaryReport ? (
                    <PaginationBlock
                      metadata={summaryReport.config.metadata}
                      onPageChange={(page) => {
                        setSearchParams((searchParams) => {
                          searchParams.set("page", page.toString());
                          return searchParams;
                        });
                      }}
                    />
                  ) : undefined
                }
              >
                <TimeOffBalancesSummaryTable
                  report={summaryReport}
                  isLoading={isLoadingSummary}
                  typedFilters={typedFilters}
                  onApplySorting={onApplySorting}
                />
              </Page.StickyLayout>
            </TabItem>
            {categories?.map((category) => (
              <TabItem
                key={category.id}
                title={category.title}
                className={"flex min-h-0 min-w-0 flex-1 flex-col"}
              >
                <Page.StickyLayout
                  toolbar={
                    <Search
                      onInput={(e) => {
                        setSearchTerm(e.currentTarget.value);
                      }}
                      type={"text"}
                      value={searchTerm}
                      placeholder={t("common:search")}
                      className={"w-96"}
                    />
                  }
                  footer={
                    byCategoryReport ? (
                      <PaginationBlock
                        metadata={byCategoryReport.config.metadata}
                        onPageChange={(page) => {
                          setSearchParams((searchParams) => {
                            searchParams.set("page", page.toString());
                            return searchParams;
                          });
                        }}
                      />
                    ) : undefined
                  }
                >
                  <TimeOffBalancesByCategoryTable
                    report={byCategoryReport}
                    isLoading={isLoadingByCategory}
                    typedFilters={typedFiltersByCategory}
                    onApplySorting={onApplySortingByCategory}
                  />
                </Page.StickyLayout>
              </TabItem>
            ))}
          </Tabs>
        </Page.Content>
      </Page>
    </>
  );
};
